import type { TypeKeyboard, TypeKeyboardCommand, TypeKeyboardHotkey, TypeKeyboardParams } from './types';

// ----------------------------------------------------------------------------

class KeyboardShortcutHandler {
    #registry: TypeKeyboard = {
        shortcuts: {},
        commands: {},
    };

    readonly #config: Required<Omit<TypeKeyboardParams, 'shortcuts' | 'commands'>> = {
        autoStart: false,
        capture: true,
        listenMessage: false,
        ignoreEditable: true,
        helpers: {},
    };

    readonly #onMessage: (evt: MessageEvent) => void;

    readonly #onKeyDown: (evt: KeyboardEvent) => void;

    constructor(params: TypeKeyboardParams = {}) {
        const { shortcuts = {}, commands = {}, ...config } = params;

        this.#registry.shortcuts = shortcuts;
        this.#registry.commands = commands;
        this.#config = {
            ...this.#config,
            ...config,
        };
        this.#onMessage = this.handleMessage.bind(this);
        this.#onKeyDown = this.handleKey.bind(this);

        if (this.#config.autoStart) {
            this.start();
        }
    }

    start(): void {
        document.removeEventListener('keydown', this.#onKeyDown, this.#config.capture);
        document.addEventListener('keydown', this.#onKeyDown, this.#config.capture);

        if (this.#config.listenMessage) {
            window.removeEventListener('message', this.#onMessage);
            window.addEventListener('message', this.#onMessage);
        }
    }

    destroy(): void {
        document.removeEventListener('keydown', this.#onKeyDown, this.#config.capture);

        if (this.#config.listenMessage) {
            window.removeEventListener('message', this.#onMessage);
        }
    }

    run(hotkey: string): void {
        this.execute(hotkey);
    }

    getRegistry(): TypeKeyboard {
        return {
            shortcuts: { ...this.#registry.shortcuts },
            commands: { ...this.#registry.commands },
        };
    }

    isHotkey(hotkey: string): boolean {
        return Object.hasOwn(this.#registry.shortcuts, hotkey);
    }

    addCommand(command: string, fn: TypeKeyboardCommand): void {
        this.#registry.commands[command] = fn;
    }

    set(hotkey: string, fn: TypeKeyboardHotkey): void {
        this.#registry.shortcuts[hotkey] = fn;
    }

    remove(hotkey: string): void {
        delete this.#registry.shortcuts[hotkey];
    }

    purge(): void {
        this.#registry = {
            shortcuts: {},
            commands: {},
        };
    }

    // ------------------------------------------------------------------------

    handleMessage(evt: MessageEvent): void {
        if (!this.#config.listenMessage || evt?.data?.myType !== 'keyboardEvent') {
            return;
        }

        const keyboardEvent = new KeyboardEvent('keydown', {
            key: evt.data.key,
            code: evt.data.code,
            repeat: evt.data.repeat,
            location: evt.data.location,
            altKey: evt.data.altKey,
            ctrlKey: evt.data.ctrlKey,
            shiftKey: evt.data.shiftKey,
            metaKey: evt.data.metaKey,
            bubbles: true,
        });

        document.dispatchEvent(keyboardEvent);
    }

    handleKey(evt: KeyboardEvent): void {
        if (this.#config.ignoreEditable && shouldIgnoreKeyboardEvent(evt)) {
            return;
        }

        const combo = getKeyboardCombo(evt);

        if (combo == null || !this.isHotkey(combo)) {
            return;
        }

        evt.preventDefault();

        this.execute(combo, evt);
    }

    execute(hotkey: string, evt?: KeyboardEvent): void {
        const item = this.#registry.shortcuts[hotkey];
        const helpers = this.#config.helpers;

        //
        if (typeof item === 'function') {
            item(evt, helpers);
        }
        //
        else if (typeof this.#registry.commands[item] === 'function') {
            this.#registry.commands[item](evt, helpers);
        }
    }
}

// ----------------------------------------------------------------------------

function shouldIgnoreKeyboardEvent(evt: KeyboardEvent): boolean {
    const target = evt.target as HTMLElement | null;
    const tag = target?.tagName;

    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || Boolean(target?.isContentEditable);
}

function getKeyboardCombo(evt: KeyboardEvent): string | null {
    const modifiers: string[] = [];

    if (evt.ctrlKey) {
        modifiers.push('ctrl');
    }

    if (evt.altKey) {
        modifiers.push('alt');
    }

    if (evt.shiftKey) {
        modifiers.push('shift');
    }

    if (evt.metaKey) {
        modifiers.push('meta');
    }

    let key = evt.key;

    if (!key) {
        return null;
    }

    if (key === 'Esc') {
        key = 'Escape';
    }

    modifiers.push(key.toLowerCase());

    return modifiers.join('+');
}

// ----------------------------------------------------------------------------

export default KeyboardShortcutHandler;
